// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the web generator, the crux of the game. A web is a pure function of
// (mode, seed, index) so every peer builds the byte-identical puzzle from the frozen seed.
//
// PLANAR BY CONSTRUCTION, so a tangle-free solution ALWAYS exists and the player is never
// stuck. We first lay out the beads in a nice spread (the SOLUTION positions), then build a
// straight-line planar graph over them: a Euclidean minimum spanning tree — whose edges
// provably never cross — connects everything, and extra edges are added only when they
// cross nothing already placed (and don't run through a third bead). That solution layout
// therefore has ZERO crossings. Finally we SCRAMBLE the beads to random positions to make
// the tangle the player sees, re-scrambling until it has enough crossings to be a puzzle
// (falling back to a random-order ring so generation is TOTAL and can never hang).
//
// `solution` is used only by the generator and the reference bot; it is never sent on the
// wire — but being a pure function of the seed, both peers could compute it anyway.

import { makeRng, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import { countCrossings, dist, pointSegDist, type Edge, type Pt } from './geom';
import { webSeed } from './seed';
import type { Mode } from './modes';

export interface Graph {
  n: number;
  edges: Edge[];
  start: Pt[]; // the scrambled layout the player begins with
  solution: Pt[]; // a crossing-free layout (generator + bot only, never on the wire)
  startCrossings: number;
  par: number; // reference "good" move count (≈ one placement per bead)
}

const MAX_DEG = 4;

/** A Poisson-ish spread of `n` points in [margin, 1-margin]², deterministic from `rng`. */
function spread(rng: Rng, n: number, minDist: number, margin: number): Pt[] {
  const pts: Pt[] = [];
  const span = 1 - 2 * margin;
  let guard = 0;
  while (pts.length < n && guard++ < n * 500) {
    const p = { x: margin + rng() * span, y: margin + rng() * span };
    if (pts.every((q) => dist(p, q) >= minDist)) pts.push(p);
  }
  while (pts.length < n) pts.push({ x: margin + rng() * span, y: margin + rng() * span });
  return pts;
}

/** All unordered index pairs, sorted by the Euclidean distance between their points. */
function pairsByDistance(pts: Pt[]): Array<[number, number]> {
  const pairs: Array<[number, number, number]> = [];
  for (let i = 0; i < pts.length; i++)
    for (let j = i + 1; j < pts.length; j++) pairs.push([i, j, dist(pts[i], pts[j])]);
  pairs.sort((a, b) => a[2] - b[2] || a[0] - b[0] || a[1] - b[1]);
  return pairs.map(([a, b]) => [a, b]);
}

class DSU {
  private p: number[];
  constructor(n: number) {
    this.p = Array.from({ length: n }, (_, i) => i);
  }
  find(x: number): number {
    while (this.p[x] !== x) x = this.p[x] = this.p[this.p[x]];
    return x;
  }
  union(a: number, b: number): boolean {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return false;
    this.p[ra] = rb;
    return true;
  }
}

function crossesExisting(pts: Pt[], edges: Edge[], a: number, b: number): boolean {
  const pa = pts[a];
  const pb = pts[b];
  for (const [c, d] of edges) {
    if (c === a || c === b || d === a || d === b) continue; // adjacency is not a crossing
    if (segCross(pa, pb, pts[c], pts[d])) return true;
  }
  return false;
}

// Local proper-crossing (kept private so graph.ts does not import the game's crossing count
// into its own construction loop — construction and the live rule stay independent).
function segCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt): number => {
    const v = (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
    return v > 1e-12 ? 1 : v < -1e-12 ? -1 : 0;
  };
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
}

/** True if the segment a→b passes too close to any bead other than its endpoints. */
function runsThroughBead(pts: Pt[], a: number, b: number): boolean {
  for (let k = 0; k < pts.length; k++) {
    if (k === a || k === b) continue;
    if (pointSegDist(pts[k], pts[a], pts[b]) < 0.045) return true;
  }
  return false;
}

function buildPlanarEdges(pts: Pt[], target: number): Edge[] {
  const pairs = pairsByDistance(pts);
  const edges: Array<[number, number]> = [];
  const deg = pts.map(() => 0);
  const dsu = new DSU(pts.length);

  // 1) Euclidean MST (Kruskal) — its edges provably never cross, so it needs no check.
  for (const [a, b] of pairs) {
    if (edges.length >= pts.length - 1) break;
    if (dsu.union(a, b)) {
      edges.push([a, b]);
      deg[a]++;
      deg[b]++;
    }
  }

  // 2) Extra planar edges: shortest first, added only when they cross nothing already
  //    placed and do not run through a third bead, respecting a degree cap.
  const has = new Set(edges.map(([a, b]) => `${a},${b}`));
  for (const [a, b] of pairs) {
    if (edges.length >= target) break;
    if (has.has(`${a},${b}`)) continue;
    if (deg[a] >= MAX_DEG || deg[b] >= MAX_DEG) continue;
    if (runsThroughBead(pts, a, b)) continue;
    if (crossesExisting(pts, edges, a, b)) continue;
    edges.push([a, b]);
    has.add(`${a},${b}`);
    deg[a]++;
    deg[b]++;
  }
  return edges;
}

/** A random-order ring placement — a connected graph laid on a ring is reliably tangled. */
function ring(rng: Rng, n: number): Pt[] {
  const order = shuffle(rng, Array.from({ length: n }, (_, i) => i));
  const pts: Pt[] = new Array(n);
  for (let k = 0; k < n; k++) {
    const ang = (k / n) * Math.PI * 2;
    pts[order[k]] = { x: 0.5 + 0.38 * Math.cos(ang), y: 0.5 + 0.38 * Math.sin(ang) };
  }
  return pts;
}

function scramble(rng: Rng, n: number, edges: Edge[], minCross: number): Pt[] {
  for (let attempt = 0; attempt < 80; attempt++) {
    const pts = spread(rng, n, 0.1, 0.1);
    if (countCrossings(pts, edges) >= minCross) return pts;
  }
  // Totality guarantee: a random-order ring almost always clears the bar; if even it does
  // not (a near-tree tiny web), it is still a valid, playable start.
  for (let attempt = 0; attempt < 20; attempt++) {
    const pts = ring(rng, n);
    if (countCrossings(pts, edges) >= minCross) return pts;
  }
  return ring(rng, n);
}

/** Build web #index of (mode, seed). Pure and deterministic. */
export function buildGraph(mode: Mode, seed: number, index = 0): Graph {
  const rng = makeRng(webSeed(seed, index));
  const n = mode.n;
  const minDist = Math.max(0.14, 0.66 / Math.sqrt(n));
  const solution = spread(rng, n, minDist, 0.12);
  const target = Math.round(n * mode.density);
  const edges = buildPlanarEdges(solution, target);
  const minCross = Math.max(3, Math.round(n * 0.6));
  const start = scramble(rng, n, edges, minCross);
  return {
    n,
    edges,
    start,
    solution,
    startCrossings: countCrossings(start, edges),
    par: n,
  };
}
